/**
 * ManaOverviewPanel — horizontal columns showing pip demand vs mana production per color.
 *
 * Layout: 5 columns side by side (one per identity color), each showing:
 *   - Color name
 *   - Pip demand % (with bar)
 *   - Land production % (with bar)
 *   - Source count vs target (x/y)
 *
 * Architecture §7, PRD §5.2.
 */

import type { Color } from '@mtg-deck-tool/shared';
import type { ManaOverviewRow } from '../../lib/land-sim/karsten-types.js';

// ─── Color constants ─────────────────────────────────────────────────────────

const COLOR_NAMES: Record<Color, string> = {
  W: 'White',
  U: 'Blue',
  B: 'Black',
  R: 'Red',
  G: 'Green',
};

const COLOR_BARS: Record<Color, string> = {
  W: 'bg-amber-100',
  U: 'bg-blue-500',
  B: 'bg-purple-800',
  R: 'bg-red-600',
  G: 'bg-green-600',
};

const PIP_BARS: Record<Color, string> = {
  W: 'bg-amber-100/40',
  U: 'bg-blue-500/40',
  B: 'bg-purple-800/40',
  R: 'bg-red-600/40',
  G: 'bg-green-600/40',
};

const COLOR_TEXT: Record<Color, string> = {
  W: 'text-amber-200',
  U: 'text-blue-300',
  B: 'text-purple-300',
  R: 'text-red-300',
  G: 'text-green-300',
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface ManaOverviewPanelProps {
  rows: ManaOverviewRow[];
  /** Previous mana base rows for delta display (e.g., before generation). */
  oldRows?: ManaOverviewRow[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ManaOverviewPanel({ rows, oldRows }: ManaOverviewPanelProps) {
  if (rows.length === 0) return null;

  const oldByColor = new Map<Color, ManaOverviewRow>();
  if (oldRows) {
    for (const r of oldRows) oldByColor.set(r.color, r);
  }

  return (
    <div className="px-4 py-3 border-b border-gray-800/60 bg-gray-900/40">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${rows.length}, 1fr)` }}
      >
        {rows.map((row) => {
          const landSources = row.landSources;
          const isMet = landSources >= row.target;
          const oldRow = oldByColor.get(row.color);

          return (
            <div key={row.color} className="flex flex-col items-center gap-1.5">
              {/* Color name */}
              <span className={`text-[11px] font-semibold ${COLOR_TEXT[row.color]}`}>
                {COLOR_NAMES[row.color]}
              </span>

              {/* Pip demand */}
              <div className="w-full">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[9px] text-gray-500">Pips</span>
                  <span className="text-[10px] text-gray-300 tabular-nums">
                    {Math.round(row.pipDemandPct)}%
                  </span>
                </div>
                <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${PIP_BARS[row.color]}`}
                    style={{ width: `${Math.min(100, row.pipDemandPct).toFixed(1)}%` }}
                  />
                </div>
              </div>

              {/* Land production */}
              <div className="w-full">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[9px] text-gray-500">Lands</span>
                  <span className="text-[10px] text-gray-300 tabular-nums">
                    {Math.round(row.productionPct)}%
                    {oldRow && (() => {
                      const delta = Math.round(row.productionPct - oldRow.productionPct);
                      if (delta === 0) return null;
                      const color = delta > 0 ? 'text-green-400' : 'text-red-400';
                      return <span className={`ml-1 ${color}`}>({delta > 0 ? '+' : ''}{delta}%)</span>;
                    })()}
                  </span>
                </div>
                <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${COLOR_BARS[row.color]}`}
                    style={{ width: `${Math.min(100, row.productionPct).toFixed(1)}%` }}
                  />
                </div>
              </div>

              {/* Land source count vs target */}
              <span className={`text-[10px] tabular-nums ${isMet ? 'text-gray-500' : 'text-amber-400'}`}>
                {landSources}/{row.target} lands
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
